import { Icon } from '#app/components/ui/icon.tsx'
import { type Route } from './+types/nationals.schedule.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'Nationals Sample Schedule | DanceMakers' },
	{
		name: 'description',
		content:
			"View sample schedules from last year's DanceMakers Nationals including event details and class schedules to help you plan your trip.",
	},
]

export default function NationalsSampleScheduleRoute() {
	return (
		<div className="min-h-screen pt-12 md:pt-16">
			{/* Hero Section */}
			<section className="container px-6 pb-8 pt-12 md:px-16 md:pb-10 md:pt-16">
				<div className="max-w-3xl">
					<p className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-primary">
						Nationals 2025
					</p>
					<h1 className="mb-6 font-display text-3xl font-bold uppercase leading-tight md:text-4xl lg:text-5xl">
						Sample Schedule
					</h1>
					<p className="text-sm leading-relaxed text-muted-foreground">
						Nationals is our biggest event of the year, spanning multiple days
						with a unique schedule structure. Below you'll find sample schedules
						from last year's Nationals to help you plan your trip.
					</p>
				</div>
			</section>

			{/* Schedule Downloads */}
			<section className="bg-muted/30 py-10 md:py-16">
				<div className="container px-6 md:px-16">
					<h2 className="mb-6 font-display text-xl font-bold uppercase sm:mb-8 sm:text-2xl md:text-3xl">
						Last Year's Schedules
					</h2>

					<p className="mb-8 max-w-3xl text-sm text-muted-foreground">
						The Nationals schedule differs significantly from our regional
						events. To give you the best idea of what to expect, we've provided
						last year's schedules as a reference. The 2026 schedule will be
						released closer to the event.
					</p>

					<div className="grid max-w-4xl gap-6 md:grid-cols-2">
						{/* Event Details PDF */}
						<a
							href="/assets/nationals-event-sample.pdf"
							target="_blank"
							rel="noopener noreferrer"
							className="group block border border-muted-foreground/30 bg-background p-6 transition-colors hover:border-primary/50 sm:p-8"
						>
							<div className="flex items-start gap-4">
								<div className="flex h-12 w-12 flex-shrink-0 items-center justify-center bg-primary/10">
									<Icon name="file-text" className="h-6 w-6 text-primary" />
								</div>
								<div className="min-w-0 flex-1">
									<h3 className="mb-2 font-display text-lg font-bold uppercase transition-colors group-hover:text-primary">
										Event Details
									</h3>
									<p className="mb-4 text-sm text-muted-foreground">
										Overview of the entire Nationals week including competition
										days, special events, and key times.
									</p>
									<span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-primary">
										<span>View PDF</span>
										<Icon
											name="arrow-right"
											className="h-4 w-4 transition-transform group-hover:translate-x-1"
										/>
									</span>
								</div>
							</div>
						</a>

						{/* Class Schedule PDF */}
						<a
							href="/assets/nationals-class-sample.pdf"
							target="_blank"
							rel="noopener noreferrer"
							className="group block border border-muted-foreground/30 bg-background p-6 transition-colors hover:border-primary/50 sm:p-8"
						>
							<div className="flex items-start gap-4">
								<div className="flex h-12 w-12 flex-shrink-0 items-center justify-center bg-primary/10">
									<Icon name="file-text" className="h-6 w-6 text-primary" />
								</div>
								<div className="min-w-0 flex-1">
									<h3 className="mb-2 font-display text-lg font-bold uppercase transition-colors group-hover:text-primary">
										Class Schedule
									</h3>
									<p className="mb-4 text-sm text-muted-foreground">
										Detailed breakdown of convention classes by day and age
										group throughout the week.
									</p>
									<span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-primary">
										<span>View PDF</span>
										<Icon
											name="arrow-right"
											className="h-4 w-4 transition-transform group-hover:translate-x-1"
										/>
									</span>
								</div>
							</div>
						</a>
					</div>
				</div>
			</section>

			{/* What to Expect */}
			<section className="py-10 md:py-16">
				<div className="container px-6 md:px-16">
					<h2 className="mb-6 font-display text-xl font-bold uppercase sm:mb-8 sm:text-2xl md:text-3xl">
						What to Expect at Nationals
					</h2>

					<div className="grid max-w-5xl gap-6 md:grid-cols-3">
						<div className="bg-muted/30 p-6">
							<h3 className="mb-3 font-display font-bold uppercase text-primary">
								Multiple Days
							</h3>
							<p className="text-sm text-muted-foreground">
								Nationals spans several days, giving dancers more class time,
								competition opportunities, and special events than regional
								weekends.
							</p>
						</div>

						<div className="bg-muted/30 p-6">
							<h3 className="mb-3 font-display font-bold uppercase text-primary">
								Full Week Convention
							</h3>
							<p className="text-sm text-muted-foreground">
								Competition participants must register for the full week
								convention to be eligible to compete at Nationals.
							</p>
						</div>

						<div className="bg-muted/30 p-6">
							<h3 className="mb-3 font-display font-bold uppercase text-primary">
								Special Events
							</h3>
							<p className="text-sm text-muted-foreground">
								Nationals features exclusive events including the DanceAgain
								Showcase, expanded scholarship auditions, and special
								performances.
							</p>
						</div>
					</div>
				</div>
			</section>

			{/* Important Note */}
			<section className="bg-muted/30 py-10 md:py-16">
				<div className="container px-6 md:px-16">
					<div className="mx-auto max-w-3xl text-center">
						<div className="mb-4 inline-flex h-12 w-12 items-center justify-center bg-primary/10">
							<Icon name="clock" className="h-6 w-6 text-primary" />
						</div>
						<h2 className="mb-4 font-display text-xl font-bold uppercase sm:text-2xl">
							2025-26 Schedule Coming Soon
						</h2>
						<p className="mb-6 text-sm text-muted-foreground">
							The official Nationals 2025-26 schedule will be released as we get
							closer to the event. The sample schedules above are from last year
							and are provided to help you understand the general flow and plan
							your trip accordingly.
						</p>
						<p className="text-sm text-muted-foreground">
							Schedules are subject to change. Check back for updates or{' '}
							<a href="/contact" className="text-primary hover:underline">
								contact us
							</a>{' '}
							with any questions.
						</p>
					</div>
				</div>
			</section>

			{/* Contact CTA */}
			<section className="py-10 md:py-16">
				<div className="container px-6 text-center md:px-16">
					<h2 className="mb-4 font-display text-xl font-bold uppercase sm:text-2xl md:text-3xl">
						Questions About Nationals?
					</h2>
					<p className="mx-auto mb-6 max-w-2xl text-sm text-muted-foreground">
						Planning your trip to Nationals? Our team is here to help with any
						scheduling questions!
					</p>
					<a
						href="/contact"
						className="inline-block border border-primary px-8 py-3 text-sm font-medium uppercase tracking-wider text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
					>
						Contact Us
					</a>
				</div>
			</section>
		</div>
	)
}
